import React, { useContext, useEffect, useRef, useState } from 'react';
import styles from './../../styles/form.module.css';
import { useForm } from 'react-hook-form';
import { factorEventTracker, getRoute } from './../../helpers/utils';
import { loginOptions } from './options';
import {
  ACCESS_EXPIRED,
  AlertConfig,
  ALREADY_VERIFIED,
  AUTH_FLOW,
  DOMAIN,
  EMAIL,
  FAIL,
  FORGOT_PASSWORD,
  GOOGLE,
  GOOGLE_AUTH2,
  INVALID_CREDENTIAL,
  INVALID_USER_ERROR,
  LOGIN,
  MESSAGE,
  ON_CHANGE,
  RESET_EMAIL,
  RESET_LINK,
  SIGNUP,
  TEMPLATE,
  TOKEN_EXPIRED,
  TOKEN_INVALID,
  URL_EXPIRED,
  VERIFICATION_SEND,
  VERIFIED,
  VERIFY_EMAIL,
  VERIFY_INVITE
} from './../../content/constants';
import {
  Info,
  FormHeader,
  Logo,
  SocialAuthProvider,
  InputField,
  Button,
  NavigateLink,
  AlertBox
} from './../../components';
import auth0 from 'auth0-js';
import { Link } from 'react-router-dom';
import { useQuery } from './../../hooks/useQuery';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../context/AuthContext';

const Login = () => {
  const { t } = useTranslation();
  const [cookies, setCookie] = useCookies([AUTH_FLOW]);
  const [isSubmitting, setSubmitting] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const [email, setEmail] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: ON_CHANGE
  });
  const alertRef = useRef();

  let query = useQuery();
  const { data } = useContext(AuthContext);
  const logoUrl = data.tenantDetail?.logo || null;
  const params = data.params;
  var captcha;
  if (params) {
    var webAuth = new auth0.WebAuth(params);
    captcha = webAuth.renderCaptcha(
      document.querySelector('.captcha-container')
    );
  }
  useEffect(() => {
    let message = query.get(MESSAGE);
    document.title = t('page_title_login');
    if (query.get(TEMPLATE) === RESET_EMAIL) {
      // The reset password link expired or successfully change password
      message = message === ACCESS_EXPIRED ? URL_EXPIRED : message;
      alertRef.current.showAlert(AlertConfig[message]);
    } else if (query.get(TEMPLATE) === VERIFY_EMAIL) {
      if (message === VERIFIED) {
        // Email is successfully Verified
        alertRef.current.showAlert(AlertConfig[VERIFIED]);
      } else if (message === ALREADY_VERIFIED) {
        // The email is already registered.
        setEmail(query.get(EMAIL));
        alertRef.current.showAlert(AlertConfig[message]);
      }
    } else if (query.get(TEMPLATE) === VERIFY_INVITE) {
      if (
        message === INVALID_USER_ERROR ||
        message === TOKEN_INVALID ||
        message === TOKEN_EXPIRED
      ) {
        alertRef.current.showAlert(AlertConfig[message]);
      }
    } else if (AlertConfig && AlertConfig[message]) {
      alertRef.current.showAlert(AlertConfig[message]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookies, query, setCookie]);

  const onSubmit = async (data) => {
    if(!captcha.getValue()) {
      setCaptchaError(loginOptions.captcha.required);
      return;
    };
    setCaptchaError('');
    setAuthFlow(EMAIL);
    setSubmitting(true);
    var auth0Client = new auth0.WebAuth(params);
    auth0Client.login(
      {
        realm: params?.connection,
        password: data?.password,
        captcha: captcha.getValue(),
        username: data?.email
      },
      function (err) {
        setSubmitting(false);
        if (err) {
          factorEventTracker({
            email: data?.email,
            channel: EMAIL,
            formType: LOGIN,
            formSubmision: FAIL
          });
          alertRef.current.showAlert(AlertConfig[INVALID_CREDENTIAL]);
        }
      }
    );
  };
  const setAuthFlow = (type) => {
    setCookie(AUTH_FLOW, `${LOGIN}-${type}`, { path: '/', domain: DOMAIN });
  };
  const providerHandler = (provider) => {
    setAuthFlow(provider);
    var auth0Client = new auth0.WebAuth(params);
    const source = provider === GOOGLE ? GOOGLE_AUTH2 : provider;
    auth0Client.authorize(
      {
        connection: source
      },
      function (err) {
        console.error('Error while login', err);
      }
    );
  };

  const resetCallback = (email, err) => {
    if (err) {
      console.error(err.message);
    } else {
      setEmail(email);
      const alertDetails =
        query.get(TEMPLATE) === RESET_EMAIL
          ? AlertConfig[RESET_LINK]
          : AlertConfig[VERIFICATION_SEND];
      alertRef.current.showAlert(alertDetails);
    }
  };

  const onClickLinkHandler = () => {
    resetPassword(query.get(EMAIL));
  };

  const resetPassword = (email) => {
    var auth0Client = new auth0.WebAuth(params);
    auth0Client.changePassword(
      {
        connection: params.connection,
        email: email
      },
      resetCallback.bind(this, email)
    );
  };
  return (
    <div
      className={`${styles.auth_wrapper} ${
        logoUrl ? styles.auth_align_center : ''
      }`}>
      <div className={styles.logo}>
        <Logo logoUrl={logoUrl} />
      </div>
      {!logoUrl ? <Info logoUrl={logoUrl} /> : <div></div>}
      <div className={styles.form_container}>
        <AlertBox
          ref={alertRef}
          onClickLink={onClickLinkHandler}
          email={email}
        />
        <form onSubmit={handleSubmit(onSubmit)} className={styles.w_90}>
          <div>
            <FormHeader heading={t('log_in_to_your_account')} />
            <InputField
              label={t('email')}
              placeholder={t('enter_email_address')}
              type="email"
              name="email"
              id="email"
              customClassName={errors?.email?.message ? 'error' : undefined}
              register={register('email', loginOptions.email)}
              data-testid="login-email-address-input-field"
            />
            <span className={styles.err_text}>{t(errors?.email?.message)}</span>
            <InputField
              label={t('password')}
              placeholder={t('enter_password')}
              type="password"
              id="password"
              customClassName={errors?.password?.message ? 'error' : ''}
              name="password"
              register={register('password', loginOptions.password)}
              data-testid="login-password-input-field"
            />
            <span className={styles.err_text}>
              {t(errors?.password?.message)}
            </span>
            <div className="captcha-container"></div>
            {!!captchaError && (
              <span className={styles.err_text}>
                {t(captchaError)}
              </span>
            )}
            <div className={styles.forgot_password_link}>
              <Link
                to={getRoute(LOGIN, FORGOT_PASSWORD)}
                data-testid="login-forgot-your-password-link">
                {t('forgot_your_password')}
              </Link>
            </div>
            <Button
              showSpinner={isSubmitting}
              label={t('login')}
              type="submit"
              testId="submit-btn"
              data-testid="login-submit-button"
            />

            <div className={styles.auth_separator}>
              <p className={styles.separator_line}>{t('or')}</p>
            </div>
            <SocialAuthProvider
              isConsole={data.isConsole}
              lable={t('login_with')}
              onClickProvider={providerHandler}
            />
            {!data.isConsole && (
              <div className={styles.navigator}>
                <NavigateLink
                  text={t('dont_have_an_account_yet')}
                  linkText={t('sign_up_here')}
                  linkUrl={getRoute(LOGIN, SIGNUP)}
                  data-testid="login-signup-link"
                />
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
